import { Switch, Route, Router } from "wouter";
import { useHashLocation } from "wouter/use-hash-location";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";
import { AuthProvider } from "./lib/auth";
import { Toaster } from "@/components/ui/toaster";
import Navbar from "./components/navbar";
import LandingPage from "./pages/landing";
// Eugene 2026-06-04 (Фрон/оркестратор — медленная загрузка в РФ без VPN):
// code-split. Раньше ВСЕ страницы импортились eager → admin-v304 (242KB) +
// dashboard (258KB) + music (170KB) и пр. ехали в ОДНОМ main-бандле каждому
// анониму. По задушенному РФ-маршруту большой бандл = «еле-еле». Landing
// (домашняя) остаётся eager — критический first-paint; остальное — lazy
// (грузится только при заходе на роут). chunkReload.ts ловит stale-chunk.
const LoginPage = lazy(() => import("./pages/login"));
const LoginPhonePage = lazy(() => import("./pages/login-phone"));
const RegisterPage = lazy(() => import("./pages/register"));
const RegisterPhonePage = lazy(() => import("./pages/register-phone"));
const PrivacyPage = lazy(() => import("./pages/privacy"));
const ConsentPage = lazy(() => import("./pages/consent"));
const OfertaPage = lazy(() => import("./pages/oferta"));
const RefundPage = lazy(() => import("./pages/refund"));
const ContactsPage = lazy(() => import("./pages/contacts"));
const LyricsPage = lazy(() => import("./pages/lyrics"));
const MusicPage = lazy(() => import("./pages/music"));
const CoversPage = lazy(() => import("./pages/covers"));
const DashboardPage = lazy(() => import("./pages/dashboard"));
const NotFoundPage = lazy(() => import("./pages/not-found"));
const ForgotPasswordPage = lazy(() => import("./pages/forgot-password"));
const PaymentSuccess = lazy(() => import("./pages/payment-result").then(m => ({ default: m.PaymentSuccess })));
const PaymentFail = lazy(() => import("./pages/payment-result").then(m => ({ default: m.PaymentFail })));
const TrackPage = lazy(() => import("./pages/track"));
const TelegramCallbackPage = lazy(() => import("./pages/telegram-callback"));
const AdminV304Page = lazy(() => import("./pages/admin-v304"));
import { AdminGate } from "./components/admin-gate";
const TemplatesPage = lazy(() => import("./pages/templates"));
const GiftCertificatesPage = lazy(() => import("./pages/gift-certificates"));
const CorporatePage = lazy(() => import("./pages/corporate"));
// Eugene 2026-06-05 Босс: встраиваемый виджет-плеер для чужих сайтов (вирусный
// трафик). STANDALONE — без Navbar/FAB/Toaster/BackgroundMusic. Грузится по
// реальному пути /embed (iframe src), а НЕ по hash — поэтому отдельная ветка
// до основного layout (см. App() ниже).
const EmbedPlayer = lazy(() => import("./pages/embed-player"));
import BackgroundMusic from "./components/background-music";
import { ErrorBoundary } from "./components/error-boundary";
// Eugene 2026-06-07 (Босс «CDN отключил» — оптимизация веса лендинга): FAB-чат
// (floating-consultant ~4.5k строк + musa-xstate-fab) тянулся EAGER в main-бандл
// на КАЖДОМ анониме, хотя для first-paint не нужен (открывается по клику). Перевели
// на lazy: грузится после первого рендера (whenIdle-prefetch) или при заходе на роут.
const FloatingConsultant = lazy(() => import("./components/floating-consultant").then(m => ({ default: m.FloatingConsultant })));
const MusaXStateFab = lazy(() => import("./components/musa-xstate-fab").then(m => ({ default: m.MusaXStateFab })));
import { getFabEngine } from "./lib/musaFabFlag";
import { ScreenDebugOverlay } from "./components/screen-debug-overlay";
import { lazy, Suspense, useEffect, useState } from "react";

// Wrapper для рендера каждой страницы внутри ErrorBoundary с именем —
// чтобы вместо чёрного экрана при runtime-ошибке показать стек.
function withBoundary(Comp: any, name: string) {
  return (params: any) => (
    <ErrorBoundary pageName={name}>
      <Comp {...params} />
    </ErrorBoundary>
  );
}

// Track visitor on page load
// Eugene 2026-06-15 (фронт-критик HIGH): весь блок в try/catch — это top-level
// module-load код ВНЕ ErrorBoundary; если screen/Intl/TextEncoder бросят на
// экзотичном WebView, модуль App не загрузится → чёрный экран.
if (typeof window !== 'undefined') {
  try {
    const sid = sessionStorage.getItem('_sid') || (Math.random().toString(36).slice(2) + Date.now().toString(36));
    sessionStorage.setItem('_sid', sid);
    // Simple fingerprint from screen + timezone + language
    const fp = [screen.width, screen.height, screen.colorDepth, Intl.DateTimeFormat().resolvedOptions().timeZone, navigator.language, navigator.hardwareConcurrency].join('|');
    const fpHash = Array.from(new TextEncoder().encode(fp)).reduce((h, b) => ((h << 5) - h + b) | 0, 0).toString(36);
    fetch('/api/track-visit', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ fingerprint: fpHash, pageUrl: location.href, sessionId: sid }) }).catch(() => {});
  } catch { /* не ломаем загрузку App */ }
}

function AppContent() {
  return (
    // Eugene 2026-05-30 (Фрон-агент 8 находок overflow на 4 страницах):
    // overflow-x-clip + max-w-full + min-w-0 на root-обёртку — глобальный
    // страховой клип от горизонтального overflow (Layout-fit-no-overlap +
    // Device-fit-100 rules). См. также #root + html/body в index.css.
    <div className="min-h-screen bg-background text-foreground overflow-x-clip max-w-full min-w-0">
      <Router hook={useHashLocation}>
        <Navbar />
        <Suspense fallback={
          <div className="min-h-screen flex items-center justify-center bg-[#0a0a17]">
            <div className="w-8 h-8 rounded-full border-[3px] border-purple-300/25 border-t-purple-300 animate-spin" />
          </div>
        }>
        <Switch>
          {/* Eugene 2026-05-25 Босс — чёрный экран iOS: «/» (LandingPage) был
              БЕЗ ErrorBoundary → любой throw в плеере/плейлисте = пустая страница.
              Оборачиваем как остальные роуты (withBoundary), чтобы вместо
              чёрного экрана показать фолбэк + залогировать стек на сервер. */}
          <Route path="/" component={withBoundary(LandingPage, "landing")} />
          {/* Eugene 2026-05-21 Босс: pair-link из мессенджеров (TG/Max) →
              открывается web Музa-чат с подгрузкой истории. Route рендерит
              LandingPage — floating-consultant читает useParams().code и
              auto-open chat с pairCode на /api/muza/chat/init. */}
          <Route path="/pair/:code" component={withBoundary(LandingPage, "landing-pair")} />
          {/* Приёмка блок 10: share/play — входные точки разосланных ссылок
              (Share-links-forever rule); краш TrackPage без boundary = пустой
              экран у получателя. Boundary как у /track/:id. */}
          <Route path="/play/:id" component={withBoundary(TrackPage, "play")} />
          <Route path="/share/:id" component={withBoundary(TrackPage, "share")} />
          <Route path="/login" component={LoginPage} />
          <Route path="/login-phone" component={LoginPhonePage} />
          <Route path="/register" component={RegisterPage} />
          <Route path="/register-phone" component={RegisterPhonePage} />
          <Route path="/register/:ref" component={RegisterPage} />
          <Route path="/r/:ref" component={RegisterPage} />
          <Route path="/privacy" component={PrivacyPage} />
          <Route path="/consent" component={ConsentPage} />
          <Route path="/terms" component={OfertaPage} />
          <Route path="/oferta" component={OfertaPage} />
          <Route path="/refund" component={RefundPage} />
          <Route path="/contacts" component={ContactsPage} />
          <Route path="/lyrics" component={LyricsPage} />
          <Route path="/music" component={MusicPage} />
          <Route path="/covers" component={CoversPage} />
          <Route path="/dashboard" component={withBoundary(DashboardPage, "dashboard")} />
          <Route path="/forgot-password" component={ForgotPasswordPage} />
          <Route path="/payment/success" component={PaymentSuccess} />
          <Route path="/payment/fail" component={PaymentFail} />
          <Route path="/track/:id" component={withBoundary(TrackPage, "track")} />
          <Route path="/telegram-callback" component={TelegramCallbackPage} />
          {/* Eugene 2026-05-30 Босс «сначала проверка потом доступ в кабинет».
              AdminGate блокирует рендер admin shell до прохождения двух гейтов
              (admin-auth + IP). НИКАКОГО мелькания admin-контента до granted —
              см. components/admin-gate.tsx + правило Admin-panel-IP-hard-gate. */}
          <Route path="/admin" component={withBoundary(() => (
            <AdminGate><AdminV304Page /></AdminGate>
          ), "admin")} />
          <Route path="/admin/v304" component={withBoundary(() => (
            <AdminGate><AdminV304Page /></AdminGate>
          ), "admin-v304")} />
          <Route path="/templates" component={withBoundary(TemplatesPage, "templates")} />
          <Route path="/gift-cert" component={withBoundary(GiftCertificatesPage, "gift-cert")} />
          {/* Eugene 2026-05-26 Босс «B2B-подсистема» — ЛК юрлица. Ссылку даёт
              Музa после register_legal_entity (corporateTools.ts cabinetUrl). */}
          <Route path="/corporate/:id" component={withBoundary(CorporatePage, "corporate")} />
          <Route component={NotFoundPage} />
        </Switch>
        </Suspense>
      </Router>
      <Toaster />
      <BackgroundMusic />
      {/* Eugene 2026-05-20 (frontend-audit fix #1) — FAB collision на /admin/v304:
          FloatingConsultant z-30 в bottom-right И MusaVoiceFab z-50 в bottom-right
          оверлэпали — Босс думал что жмёт чат-кнопку Музы, попадал в voice-FAB
          → открывался voice-панель → «диалог не происходит». ФИКС: на admin-pages
          FloatingConsultant скрыт (у админа есть MusaVoiceFab для admin-задач). */}
      <AdminAwareFloatingConsultant />
      {/* Eugene 2026-05-30 Босс на iPad без DevTools — on-screen debug overlay.
          Активируется через ?debug=1 в URL или localStorage["muzaai-screen-debug"]="1".
          Prod юзеры не видят без флага. См. screen-debug-overlay.tsx. */}
      <ScreenDebugOverlay />
      {/* Eugene 2026-05-20 Босс: маленькую Музу (WalkingMusa) убрали с сайта —
          остаётся только большая в bottom-right (FloatingConsultant). */}
    </div>
  );
}

function AdminAwareFloatingConsultant() {
  // useHashLocation в Router'е, поэтому слушаем hashchange напрямую —
  // useLocation из wouter без Router context даёт path-routing, не hash.
  const [hash, setHash] = useState<string>(() => (typeof window !== "undefined" ? window.location.hash : ""));
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = () => setHash(window.location.hash);
    window.addEventListener("hashchange", handler);
    return () => window.removeEventListener("hashchange", handler);
  }, []);
  // Eugene 2026-06-01 Шаг 7 миграции на XState: выбор движка FAB по
  // feature-флагу MUSA_FAB_ENGINE (LS / ?fab=). Default 'legacy' — все
  // юзеры без opt-in остаются на проверенном FloatingConsultant. Переключение
  // на 'xstate' одним localStorage.setItem или ?fab=xstate — без деплоя.
  // Считываем один раз на mount: переключение требует перезагрузки страницы,
  // что соответствует природе feature-флага (не «горячая» смена движка).
  const [fabEngine] = useState(() => getFabEngine());
  // Префетч FAB-чанка после first-paint (idle): чат-кнопка готова к клику, но
  // не утяжеляет первичную загрузку. Не на /admin (там FAB скрыт).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const p = (window.location.hash.replace(/^#/, "") || "/");
    if (p.startsWith("/admin")) return;
    const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void }).requestIdleCallback;
    const prefetch = () => {
      (fabEngine === "xstate"
        ? import("./components/musa-xstate-fab")
        : import("./components/floating-consultant")
      ).catch(() => undefined);
    };
    if (ric) ric(prefetch, { timeout: 4000 }); else setTimeout(prefetch, 1500);
  }, [fabEngine]);
  const path = hash.replace(/^#/, "") || "/";
  const isAdminPage = path.startsWith("/admin");
  if (isAdminPage) return null;
  // FAB лениво грузится (см. lazy-импорты выше) — fallback null, чтобы не было
  // спиннера/мелькания: чат-кнопка просто появляется когда чанк дойдёт.
  return (
    <Suspense fallback={null}>
      {fabEngine === "xstate" ? <MusaXStateFab /> : <FloatingConsultant />}
    </Suspense>
  );
}

// Eugene 2026-06-05 Босс: /embed — голый виджет-плеер для встраивания в чужой
// iframe. Виджет грузится по РЕАЛЬНОМУ пути /embed (SPA-fallback отдаёт
// index.html), поэтому проверяем pathname ДО основного layout. Возвращаем
// ТОЛЬКО <EmbedPlayer/> — без Navbar, Музa-FAB, Toaster, BackgroundMusic,
// Router и трекинга визита (виджет должен быть максимально лёгким и изолированным).
function isEmbedPath(): boolean {
  if (typeof window === "undefined") return false;
  const p = window.location.pathname || "";
  return p === "/embed" || p === "/embed/";
}

function App() {
  if (isEmbedPath()) {
    return (
      <QueryClientProvider client={queryClient}>
        <Suspense
          fallback={
            <div className="min-h-screen flex items-center justify-center bg-[#0a0a17]">
              <div className="w-7 h-7 rounded-full border-[3px] border-purple-300/25 border-t-purple-300 animate-spin" />
            </div>
          }
        >
          <ErrorBoundary pageName="embed">
            <EmbedPlayer />
          </ErrorBoundary>
        </Suspense>
      </QueryClientProvider>
    );
  }
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
